import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { BodyMeasurement, MeasurementKey } from '../types';
import { ALL_MEASUREMENT_KEYS, MEASUREMENT_KINDS } from '../utils/bodyMeasurements';

interface MeasurementsChartProps {
  data: BodyMeasurement[];
  // Отслеживаемые замеры клиента — остальные на графике не показываются
  tracked?: MeasurementKey[];
}

const MeasurementsChart: React.FC<MeasurementsChartProps> = ({ data, tracked = ALL_MEASUREMENT_KEYS }) => {
  // Только отслеживаемые замеры, по которым есть хотя бы одно значение
  const kinds = MEASUREMENT_KINDS.filter(k => tracked.includes(k.key) && data.some(d => d[k.key] != null));

  // Сортируем по дате и отбрасываем записи без отслеживаемых замеров
  const sortedData = [...data]
    .filter(d => kinds.some(k => d[k.key] != null))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (sortedData.length === 0) return null;

  // Форматируем данные для Recharts
  const chartData = sortedData.map(item => ({
    date: format(new Date(item.date), 'dd.MM.yy', { locale: ru }),
    neck: item.neck,
    chest: item.chest,
    waist: item.waist,
    hips: item.hips,
    originalDate: item.date
  }));

  // Статистика по замеру — только по записям, где он снимался
  const getStats = (key: MeasurementKey) => {
    const values = sortedData.map(d => d[key]).filter((v): v is number => v != null);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const change = values.length > 1 ? values[values.length - 1] - values[0] : 0;
    return { min, max, change };
  };

  const formatMeasurement = (value: number) => `${value.toFixed(1)} см`;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.filter((entry: any) => entry.value != null).map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: <span className="font-bold">{formatMeasurement(entry.value)}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">График замеров тела</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
          {kinds.map(kind => {
            const stats = getStats(kind.key);
            return (
              <div key={kind.key}>
                <span className="font-medium" style={{ color: kind.color }}>{kind.label}:</span>
                <div>Мин: {formatMeasurement(stats.min)}</div>
                <div>Макс: {formatMeasurement(stats.max)}</div>
                {stats.change !== 0 && (
                  <div className={`font-medium ${stats.change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Изм.: {stats.change > 0 ? '+' : ''}{formatMeasurement(stats.change)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 15, right: 30, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={40}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
          />
          <YAxis
            tickFormatter={formatMeasurement}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
            domain={['dataMin - 2', 'dataMax + 2']}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} />

          {kinds.map(kind => (
            <Line
              key={kind.key}
              type="monotone"
              dataKey={kind.key}
              name={kind.label}
              stroke={kind.color}
              strokeWidth={2}
              connectNulls
              dot={{ fill: kind.color, strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5, stroke: '#ffffff', strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default MeasurementsChart;
